import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  AccountBalanceRounded,
  CloseRounded,
  ExitToAppRounded,
  HomeOutlined,
  KeyboardArrowDown,
  NotificationsNoneRounded,
  PersonOutline,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Logo from '../../assets/horizontal-white-logo.svg';
import LogoWhiteRed from '../../assets/logo-white-red.svg';

import {
  Card,
  CompanyName,
  Date,
  HeaderContainer,
  Section,
  SectionHeader,
  TextMenu,
  Title,
  TitleCard,
  Warning,
} from './styles';
import useAuthStore from '../../store/auth';
import { signOutRequest } from '../../services';
import {
  getNotificationsByUserAndCompanyIdRequest,
  removeNotification,
  updateNotificationToRead,
} from '../../services/requests/notifications/utils';
import useCompanyStore from '../../store/companies';
import { Notification } from '../../services/requests/notifications';
import useNotificationStore from '../../store/notifications';

export function Header() {
  const navigate = useNavigate();
  const signOut = useAuthStore(state => state.signOut);
  const user = useAuthStore(state => state.user);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const setMenuSelected = useAuthStore(state => state.setMenuSelected);
  const notifications = useNotificationStore(state => state.notifications);
  const setNotifications = useNotificationStore(
    state => state.setNotifications,
  );
  const countNotification = useNotificationStore(
    state => state.countNotification,
  );
  const setCountNotification = useNotificationStore(
    state => state.setCountNotification,
  );

  const [startAnimation, setStartAnimation] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [listagemNotificacoes, setListagemNotificacoes] = useState<
    Notification[]
  >([]);
  const [countNotificationsActivesUnread, setCountNotificationsActivesUnread] =
    useState<number>();
  const [listagemNotificacoesAtivas, setListagemNotificacoesAtivas] = useState<
    Notification[]
  >([]);
  const open = Boolean(anchorEl);
  const openNav = Boolean(anchorElNav);

  const handleClickOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccounts = () => {
    setAnchorEl(null);
    setMenuSelected('account');
    navigate('contas');
  };

  const handleSignOut = () => {
    signOut();
    signOutRequest();
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleGoBackToPortal = useCallback(() => {
    setMenuSelected('portal');
    navigate('reunioes');
    setAnchorEl(null);
  }, [navigate, setMenuSelected]);

  const handleGoToProfile = useCallback(() => {
    setMenuSelected('profile');
    navigate('dados-pessoais');
    setAnchorEl(null);
  }, [navigate, setMenuSelected]);

  const handleNavigateToNotifications = useCallback(() => {
    setMenuSelected('portal');
    navigate('notificacoes');
    setAnchorElNav(null);
  }, [navigate, setMenuSelected]);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await getNotificationsByUserAndCompanyIdRequest(
        user?.id as string,
        selectedCompany?.id as string,
        '1000',
        '1',
      );

      if (response?.status === 200) {
        setListagemNotificacoes(response?.data);
      }
    } catch (err) {
      console.log(err);
    }
  }, [selectedCompany?.id, user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadNotifications();
    }
  }, [user?.id]);

  useEffect(() => {
    if (listagemNotificacoes) {
      const listActives = listagemNotificacoes.filter(
        n => n.canceled_at === null,
      );

      setListagemNotificacoesAtivas(listActives);
      setNotifications(listActives);
    }
  }, [loadNotifications, listagemNotificacoes]);

  useEffect(() => {
    if (listagemNotificacoesAtivas) {
      const listActives = listagemNotificacoes.filter(
        n => n.canceled_at === null,
      );
      const countActivesUnread = listActives.filter(n => !n.read).length;
      setCountNotificationsActivesUnread(countActivesUnread);
      setCountNotification(countActivesUnread);
    }
  }, [loadNotifications, listagemNotificacoesAtivas]);

  const handleReadAll = useCallback(async () => {
    try {
      const listWithUnRead = listagemNotificacoesAtivas.filter(n => !n.read);

      const response = Promise.all(
        listWithUnRead.map(async n => {
          await updateNotificationToRead(
            n.id,
            user?.id as string,
            selectedCompany?.id as string,
          );
        }),
      );

      if (await response) {
        loadNotifications();
      }
    } catch (err) {
      console.log(err);
    }
  }, [
    listagemNotificacoesAtivas,
    loadNotifications,
    selectedCompany?.id,
    user?.id,
  ]);

  const handleRemoveAll = useCallback(async () => {
    try {
      const listWithRead = listagemNotificacoesAtivas.filter(n => n.read);

      const response = Promise.all(
        listWithRead.map(async n => {
          await removeNotification(
            n.id,
            user?.id as string,
            selectedCompany?.id as string,
          );
        }),
      );

      if (await response) {
        loadNotifications();
      }
    } catch (err) {
      console.log(err);
    }
  }, [
    listagemNotificacoesAtivas,
    loadNotifications,
    selectedCompany?.id,
    user?.id,
  ]);

  const handleChangeToRead = useCallback(
    async (notification: Notification) => {
      try {
        if (!notification.read) {
          const response = await updateNotificationToRead(
            notification?.id,
            user?.id as string,
            selectedCompany?.id as string,
          );

          if (response?.status === 200) {
            loadNotifications();
            navigate(response.data.path);
          }
        }
      } catch (err) {
        console.log(err);
      }
    },
    [loadNotifications, navigate, selectedCompany?.id, user?.id],
  );

  const handleRemoveNotification = useCallback(
    async (id: string) => {
      try {
        const response = await removeNotification(
          id,
          user?.id as string,
          selectedCompany?.id as string,
        );

        if (response?.status === 200) {
          setStartAnimation(true);
          loadNotifications();
        }
      } catch (err) {
        console.log(err);
      }
    },
    [loadNotifications, selectedCompany?.id, user?.id],
  );

  return (
    <HeaderContainer>
      <Section>
        <Button onClick={handleGoBackToPortal} sx={{ padding: '0' }}>
          <img src={Logo} className="logo" alt="logo" />
        </Button>
        <Badge badgeContent={countNotification} color="secondary">
          <IconButton
            className="btn-logout"
            aria-label="more"
            id="long-button"
            aria-controls={openNav ? 'long-menu' : undefined}
            aria-expanded={openNav ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
            sx={{ padding: '0' }}
          >
            <NotificationsNoneRounded
              sx={{
                color: '#F5F5F5',
                fontSize: '24px',
                ':hover': { filter: 'brightness(0.8)' },
              }}
            />
          </IconButton>
        </Badge>
        <Menu
          id="long-menu"
          MenuListProps={{
            'aria-labelledby': 'long-button',
            sx: { width: '100%', padding: '0' },
          }}
          anchorEl={anchorElNav}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={openNav}
          onClose={handleCloseNavMenu}
          PaperProps={{
            style: {
              width: '390px',
              maxHeight: '529px',
              padding: '0',
              display: 'flex',
              alignItems: 'start',
              marginTop: '10px',
            },
          }}
        >
          <SectionHeader sx={{ padding: '20px 25px' }}>
            <Title>Notificações</Title>
            <Box display="flex" flexDirection="row" gap="20px">
              {countNotification && countNotification > 0 ? (
                <Button
                  onClick={handleReadAll}
                  sx={{
                    fontWeight: '400',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#0B79D0',
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    letterSpacing: '0.15px',
                    textTransform: 'initial',
                  }}
                >
                  Marcar como lidas {`(${countNotification})`}
                </Button>
              ) : null}
              {!countNotification &&
              notifications &&
              notifications.length > 0 ? (
                <Button
                  onClick={handleRemoveAll}
                  sx={{
                    fontWeight: '400',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#d62237',
                    fontFamily: 'Roboto',
                    fontStyle: 'normal',
                    letterSpacing: '0.15px',
                    textTransform: 'initial',
                  }}
                >
                  Remover notificações {`(${notifications.length})`}
                </Button>
              ) : null}
            </Box>
          </SectionHeader>
          {notifications && notifications.length > 0 ? (
            notifications.slice(0, 5).map((n, idx) => (
              <MenuItem key={n?.id} sx={{ padding: '0' }}>
                <Card
                  sx={{
                    borderLeft: n.read ? 'none' : '3px solid #206da7',
                    cursor: n.read ? 'default' : 'pointer',
                    justifyContent: n.read ? 'space-between' : 'flex-start',
                  }}
                  onClick={() => handleChangeToRead(n)}
                >
                  <Box width={n.read ? '85%' : '100%'}>
                    <Date sx={{ color: n.read ? '#9E9E9E' : '#A1B2DE' }}>
                      {dayjs(n.created_at).format('DD/MM/YYYY - HH:mm')}
                    </Date>
                    <Tooltip title={n.description}>
                      <TitleCard sx={{ color: n.read ? '#616161' : '#206da7' }}>
                        {n.description}
                      </TitleCard>
                    </Tooltip>
                    {/* <Description sx={{ color: n.read ? '#9E9E9E' : '#206da7' }}>
                      Teste
                    </Description> */}
                  </Box>
                  {n.read ? (
                    <Button
                      onClick={() => handleRemoveNotification(n.id)}
                      sx={{ minWidth: '0' }}
                    >
                      <CloseRounded />
                    </Button>
                  ) : null}
                </Card>
              </MenuItem>
            ))
          ) : (
            <Warning>Nenhuma notificação não lida</Warning>
          )}
          <MenuItem sx={{ padding: '0', background: '#EEEEEE' }}>
            <Button
              onClick={handleNavigateToNotifications}
              sx={{
                width: '100%',
                color: '#323232',
                fontWeight: '700',
                height: '48px',
              }}
            >
              {`Ver todas (${listagemNotificacoesAtivas.length})`}
            </Button>
          </MenuItem>
        </Menu>
      </Section>
      <section className="login">
        <IconButton
          className="btn-logout"
          aria-label="more"
          id="long-button"
          aria-controls={open ? 'long-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleClickOpen}
        >
          <Box width="85%">
            <Tooltip title={user?.name}>
              <p
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  fontSize: '16px',
                  lineHeight: '24px',
                  textAlign: 'start',
                }}
              >
                {user?.name}
              </p>
            </Tooltip>
            {/* <CompanyName>
              {selectedCompany?.fancy_name ?? selectedCompany?.name}
            </CompanyName> */}
          </Box>
          <KeyboardArrowDown />
        </IconButton>
        <Menu
          id="long-menu"
          MenuListProps={{
            'aria-labelledby': 'long-button',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            style: {
              width: '307px',
              padding: '20px 30px',
              display: 'flex',
              alignItems: 'start',
              marginTop: '7px',
            },
          }}
        >
          <MenuItem
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              height: '45px',
              padding: '5px 10px',
              gap: '5px',
            }}
            onClick={handleGoBackToPortal}
          >
            <HomeOutlined sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            <TextMenu>PORTAL DE GOVERNANÇA</TextMenu>
          </MenuItem>
          {user?.profile === 'UN1' || user?.profile === 'UN2' ? (
            <MenuItem
              onClick={handleAccounts}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '45px',
                width: '100%',
                padding: '5px 10px',
                gap: '5px',
              }}
            >
              <AccountBalanceRounded sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
              <TextMenu>CONTA / EMPRESA</TextMenu>
            </MenuItem>
          ) : null}
          <MenuItem
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              height: '45px',
              padding: '5px 10px',
              gap: '5px',
            }}
            onClick={handleGoToProfile}
          >
            <PersonOutline sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            <TextMenu>PERFIL</TextMenu>
          </MenuItem>
          <MenuItem
            onClick={handleSignOut}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              alignItems: 'center',
              height: '45px',
              padding: '5px 10px',
              gap: '5px',
            }}
          >
            <ExitToAppRounded sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
            <TextMenu>SAIR</TextMenu>
          </MenuItem>
        </Menu>
      </section>
    </HeaderContainer>
  );
}
